import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAdminContent } from "@/context/AdminContentContext";
import { Panel } from "@/components/admin/Field";
import { Switch } from "@/components/ui/switch";

export function DiscordGalleryPanel() {
  const { draft, update } = useAdminContent();
  const auto = draft.media.gallery_source === "discord";
  const channel = draft.discord?.channel_media || "";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/discord/media").then(({ data: d }) => setData(d)).catch(() => setData({ items: [], error: "unavailable" }));
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/discord/media/refresh");
      setData(d);
      toast.success(`${d.items.length} imagens sincronizadas do Discord.`);
    } catch (err) {
      toast.error(errorMessage(err, "Não foi possível sincronizar."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Galeria automática do Discord" description="Em vez de carregares fotografias aqui, a galeria lê as imagens publicadas no canal de media do Discord. Sempre que alguém publica uma foto, o site atualiza sozinho em menos de 1 minuto.">
      <div className="flex items-center justify-between gap-4 rounded-md border border-eden-border bg-eden-bg p-4" data-testid="gallery-source-row">
        <div>
          <p className="text-sm font-medium text-eden-text">Usar imagens do canal de media do Discord</p>
          <p className="text-xs text-eden-muted">{auto ? "Ligado: a galeria mostra as imagens do Discord. As imagens carregadas abaixo ficam guardadas mas não aparecem." : "Desligado: a galeria usa as imagens carregadas manualmente abaixo."}</p>
        </div>
        <Switch checked={auto} onCheckedChange={(v) => update("media", { gallery_source: v ? "discord" : "manual" })} aria-label="Galeria automática do Discord" data-testid="gallery-source-switch" />
      </div>
      {!channel && <p className="text-sm text-amber-400" data-testid="gallery-discord-no-channel">Define o ID do canal de media em Ligações & IDs.</p>}
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-eden-secondary" data-testid="gallery-discord-status">
          {!data && "A ler o canal…"}
          {data?.error === "not_configured" && "Canal ou token do bot não configurados."}
          {data?.error === "unavailable" && !data.stale && "O bot não tem acesso a este canal (Missing Access). No Discord: Definições do canal → Permissões → adiciona o bot (ou o seu cargo) com “Ver canal” e “Ler histórico de mensagens”."}
          {data?.stale && <span className="text-amber-400" data-testid="gallery-discord-stale">O bot perdeu o acesso ao canal (Missing Access). O site continua a mostrar as últimas {data.items.length} imagens guardadas. Repõe a permissão “Ver canal” + “Ler histórico” ao cargo EDEN SITE na categoria do canal.</span>}
          {data && !data.error && `${data.items.length} imagens encontradas no canal.`}
        </p>
        <button type="button" onClick={refresh} disabled={busy} className="btn-ghost h-8 text-xs" data-testid="gallery-discord-refresh"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Atualizar agora</button>
      </div>
      {data?.items?.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="gallery-discord-preview">
          {data.items.slice(0, 12).map((it) => (
            <li key={it.id} className="aspect-video overflow-hidden rounded-sm border border-eden-border bg-eden-bg">
              <img src={it.url} alt={it.caption || "Imagem do Discord"} loading="lazy" className="h-full w-full object-cover" />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
